/**
 * Module dependencies.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NoteEditor } from './editor/note-editor';
import { useUploadImage } from 'app/hooks/requests/use-upload-image';
import { useTranslation } from 'react-i18next';
import { ActionsProvider } from './actions-provider';
import { Files } from './files';
import { NoteList } from './note-list';
import { NoteFile, useGetNotesFileSystem } from 'app/hooks/requests/apps-notes/use-get-notes-file-system';
import { NotesSelection } from './types';
import { useGetNotesContent } from 'app/hooks/requests/apps-notes/use-get-notes-content';
import { usePostNotesEditItem } from 'app/hooks/requests/apps-notes/use-post-edit-item';
import { useNoteBacklinks } from 'app/hooks/requests/apps-notes/use-note-links';
import { useNotesSearch } from 'app/hooks/requests/apps-notes/use-notes-search';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, Copy, Download, Link2, NotebookText, Star } from 'lucide-react';
import { Spinner } from 'app/components/ui/spinner';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useSetBreadcrumbTrail } from 'app/components/templates/dashboard/app-shell/breadcrumb-provider';
import { cn } from 'app/lib/utils';

/**
 * Tree helpers.
 */

function nodeName(node: NoteFile): string {
  return typeof node.title === 'string' ? node.title : '';
}

function findNode(tree: NoteFile[], key: string): NoteFile | undefined {
  for (const node of tree) {
    if (node.key === key) {
      return node;
    }

    if (node.children) {
      const found = findNode(node.children, key);

      if (found) {
        return found;
      }
    }
  }

  return undefined;
}

function collectFiles(nodes: NoteFile[]): NoteFile[] {
  return nodes.flatMap(node => (node.isLeaf ? [node] : node.children ? collectFiles(node.children) : []));
}

function flatFolders(nodes: NoteFile[]): { key: string; name: string }[] {
  return nodes.flatMap(node =>
    node.isLeaf ? [] : [{ key: node.key, name: nodeName(node) }, ...(node.children ? flatFolders(node.children) : [])]
  );
}

function byUpdatedDesc(a: NoteFile, b: NoteFile): number {
  return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
}

/**
 * `SaveStatus` component.
 */

function SaveStatus({
  onRetry,
  savedAt,
  state
}: {
  onRetry: () => void;
  savedAt: Date | null;
  state: 'idle' | 'saving' | 'error';
}): JSX.Element | null {
  const [translate] = useTranslation();

  if (state === 'saving') {
    return <span className={'text-xs text-muted-foreground'}>{translate('notes.save.saving')}</span>;
  }

  if (state === 'error') {
    return (
      <button className={'text-xs font-medium text-destructive hover:underline'} onClick={onRetry} type={'button'}>
        {translate('notes.save.failed')}
      </button>
    );
  }

  if (savedAt) {
    return (
      <span className={'text-xs text-muted-foreground'}>
        {translate('notes.save.savedAt', {
          time: savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        })}
      </span>
    );
  }

  return null;
}

/**
 * Export `Notes` component — three-column workspace (library · note list ·
 * editor), aligned with the contab dashboard look.
 */

export function Notes(): JSX.Element {
  const [translate] = useTranslation();
  const { success } = useToast();
  const { mutateAsync: uploadImage } = useUploadImage();
  const { data: tree } = useGetNotesFileSystem();
  const [value, setValue] = useState<string | null>(null);
  const [key, setKey] = useState<React.Key | null>(null);
  const [selection, setSelection] = useState<NotesSelection>({ type: 'all' });
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [mobileView, setMobileView] = useState<'library' | 'list' | 'editor'>('library');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const loadedRef = useRef<string | null>(null);
  const appliedKeyRef = useRef<React.Key | null>(null);
  const { data: fileContent } = useGetNotesContent(key);
  const editMutation = usePostNotesEditItem();
  const { mutateAsync: metaMutation } = usePostNotesEditItem();
  const { data: backlinks } = useNoteBacklinks(key as string | null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 200);

    return () => clearTimeout(timer);
  }, [query]);

  const searchActive = debounced.trim().length >= 2;
  const { data: searchResults, isFetching: isSearching } = useNotesSearch(debounced);

  // Deep-link: open a specific note via ?note=<id> (from the entity Notes
  // panels). Consume the param so it doesn't re-fire on later selections.
  useEffect(() => {
    const noteParam = searchParams.get('note');

    if (noteParam) {
      setKey(noteParam);
      setMobileView('editor');
      searchParams.delete('note');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switching notes: blank the editor until the new note's content arrives.
  useEffect(() => {
    setValue(null);
    appliedKeyRef.current = null;
  }, [key]);

  // Load the server content once per note. Refetches after an autosave are
  // ignored on purpose: the editor already holds the latest text, and pushing
  // the (possibly older) server copy back in would lose keystrokes typed while
  // the save was in flight and reset the caret.
  useEffect(() => {
    if (!fileContent || !key || appliedKeyRef.current === key) {
      return;
    }

    const content = (fileContent as any)?.content || '';

    appliedKeyRef.current = key;
    setValue(content);
    loadedRef.current = content;
    setSavedAt(null);
  }, [fileContent, key]);

  const save = useCallback(async () => {
    if (!key) {
      return;
    }

    try {
      await editMutation.mutateAsync({ content: value, id: key });

      loadedRef.current = value;
      setSavedAt(new Date());
    } catch (error) {
      // Surfaced by the save indicator.
    }
  }, [editMutation, key, value]);

  useEffect(() => {
    if (!key || value === loadedRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      save();
    }, 1000);

    return () => clearTimeout(timer);
  }, [key, save, value]);

  const openNote = useCallback((selected: string) => {
    setKey(selected);
    setMobileView('editor');
  }, []);

  const handleSelectionChange = useCallback((next: NotesSelection) => {
    setSelection(next);
    setMobileView('list');
  }, []);

  // Middle-column notes for the active selection.
  const listNotes = useMemo<NoteFile[]>(() => {
    const nodes = tree ?? [];

    switch (selection.type) {
      case 'folder': {
        const folder = findNode(nodes, selection.key);

        return (folder?.children ?? []).filter(node => node.isLeaf).sort(byUpdatedDesc);
      }

      case 'favorites':
        return collectFiles(nodes)
          .filter(node => node.favorite)
          .sort(byUpdatedDesc);

      case 'recent':
        return collectFiles(nodes)
          .filter(node => node.updatedAt)
          .sort(byUpdatedDesc)
          .slice(0, 30);

      case 'trash':
        return [];

      default:
        return collectFiles(nodes).sort(byUpdatedDesc);
    }
  }, [selection, tree]);

  const listTitle = useMemo(() => {
    switch (selection.type) {
      case 'folder':
        return selection.name || translate('notes.list.allNotes');
      case 'favorites':
        return translate('notes.sections.favorites');
      case 'recent':
        return translate('notes.sections.recent');
      case 'trash':
        return translate('notes.trash.title');
      default:
        return translate('notes.list.allNotes');
    }
  }, [selection, translate]);

  const folders = useMemo(() => flatFolders(tree ?? []), [tree]);
  const parentIdForNew = selection.type === 'folder' ? selection.key : null;

  const currentName = (fileContent as any)?.name as string | undefined;
  const currentNode = tree && key ? findNode(tree, key as string) : undefined;
  const isFavorite = !!currentNode?.favorite;
  const saveState = editMutation.isLoading ? 'saving' : editMutation.isError ? 'error' : 'idle';

  // Publish the open note's name to the dashboard breadcrumb (Notes > … > name).
  useSetBreadcrumbTrail(key ? currentName : null);

  const handleToggleFavorite = useCallback(async () => {
    if (!key) {
      return;
    }

    try {
      await metaMutation({ favorite: !isFavorite, id: key as string });
    } catch (error) {
      // Non-critical.
    }
  }, [isFavorite, key, metaMutation]);

  const handleDownload = useCallback(() => {
    const safeName = (currentName || 'note').replace(/[\\/:*?"<>|]/g, '_');
    const blob = new Blob([value ?? ''], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = safeName.endsWith('.md') ? safeName : `${safeName}.md`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [currentName, value]);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(value ?? '');
    success({ message: translate('notes.export.copied') });
  }, [success, translate, value]);

  return (
    <ActionsProvider>
      {/* Break out of the dashboard card's inner padding so the columns sit
          flush to the card edges (no stray padding around the workspace). */}
      <div
        className={'-mx-5 -mb-5 -mt-5 flex min-h-0 overflow-hidden rounded-b-xl md:-mx-6'}
        style={{ height: 'calc(100vh - 110px)' }}
      >
        {/* Column 1 — Library (navigation only) */}
        <aside
          className={cn(
            'w-full shrink-0 flex-col bg-muted/30 p-3 md:flex md:w-[248px] md:border-r md:border-border',
            mobileView === 'library' ? 'flex' : 'hidden'
          )}
        >
          <Files
            onQueryChange={setQuery}
            onSelectionChange={handleSelectionChange}
            parentIdForNew={parentIdForNew}
            query={query}
            selection={selection}
          />
        </aside>

        {/* Column 2 — Note list */}
        <section
          className={cn(
            'w-full shrink-0 flex-col bg-background md:flex md:w-[320px] md:border-r md:border-border',
            mobileView === 'list' ? 'flex' : 'hidden'
          )}
        >
          <button
            className={'flex items-center gap-1 px-3 pt-3 text-sm text-muted-foreground md:hidden'}
            onClick={() => setMobileView('library')}
            type={'button'}
          >
            <ChevronLeft className={'h-4 w-4'} />

            {translate('notes.backToLibrary')}
          </button>

          <NoteList
            folders={folders}
            isSearching={isSearching}
            notes={listNotes}
            onSelect={openNote}
            query={debounced}
            searchActive={searchActive}
            searchResults={searchResults}
            selectedKey={key as string | null}
            selection={selection}
            title={listTitle}
          />
        </section>

        {/* Column 3 — Editor */}
        <main
          className={cn('min-w-0 flex-1 flex-col bg-background md:flex', mobileView === 'editor' ? 'flex' : 'hidden')}
        >
          {!key ? (
            <div className={'flex flex-1 flex-col items-center justify-center text-center text-muted-foreground'}>
              <NotebookText className={'mb-3 h-10 w-10 opacity-40'} />

              <p className={'text-sm'}>{translate('notes.selectPrompt')}</p>
            </div>
          ) : (
            <div className={'flex min-h-0 w-full flex-1 flex-col px-5 py-5 md:px-8'}>
              <div className={'mb-3 flex items-center gap-2 md:hidden'}>
                <button
                  className={'flex items-center gap-1 text-sm text-muted-foreground'}
                  onClick={() => setMobileView('list')}
                  type={'button'}
                >
                  <ChevronLeft className={'h-4 w-4'} />

                  {translate('notes.backToList')}
                </button>
              </div>

              <div className={'flex items-center gap-3'}>
                <SaveStatus onRetry={save} savedAt={savedAt} state={saveState} />

                <div className={'ml-auto flex items-center gap-1'}>
                  <button
                    className={cn(
                      'rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                      isFavorite && 'text-amber-500 hover:text-amber-500'
                    )}
                    onClick={handleToggleFavorite}
                    title={translate('notes.sections.favorites')}
                    type={'button'}
                  >
                    <Star className={cn('h-4 w-4', isFavorite && 'fill-amber-400')} />
                  </button>

                  <button
                    className={
                      'rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                    }
                    onClick={handleCopy}
                    title={translate('notes.export.copy')}
                    type={'button'}
                  >
                    <Copy className={'h-4 w-4'} />
                  </button>

                  <button
                    className={
                      'rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                    }
                    onClick={handleDownload}
                    title={translate('notes.export.download')}
                    type={'button'}
                  >
                    <Download className={'h-4 w-4'} />
                  </button>
                </div>
              </div>

              {!!backlinks && backlinks.length > 0 && (
                <div className={'mt-3 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground'}>
                  <Link2 className={'h-3.5 w-3.5'} />

                  <span>{translate('notes.backlinks.label')}</span>

                  {backlinks.map(link => (
                    <span className={'rounded-full bg-muted px-2 py-0.5 font-medium text-foreground'} key={link.linkId}>
                      {translate(`notes.entities.${link.entityType}`)}
                    </span>
                  ))}
                </div>
              )}

              <div className={'mt-4 flex min-h-0 flex-1 flex-col'}>
                {value === null ? (
                  <div className={'grid flex-1 place-items-center'}>
                    <Spinner />
                  </div>
                ) : (
                  <NoteEditor key={String(key)} onChange={setValue} uploadImage={uploadImage} value={value} />
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </ActionsProvider>
  );
}
